import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import stockimg2 from "../assets/stockimg2.jpg";
import stockimg3 from "../assets/stockimg3.jpg";
import stockimg4 from "../assets/stockimg4.jpg";
import stockimg5 from "../assets/stockimg5.jpg";
import { useNavigate } from "react-router-dom";

// Static SVG components to avoid recreating them on each render
const CheckIcon = () => (
  <svg className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
  </svg>
);

const StarIcon = () => (
  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const UserAddIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

function Content() {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  // Memoize animation variants to prevent recreation on every render
  const animationVariants = useMemo(() => ({
    container: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.2,
          delayChildren: 0.3
        }
      }
    },
    item: {
      hidden: { y: 30, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          duration: 0.6,
          ease: "easeOut"
        }
      }
    },
    imageAnim: {
      hidden: { x: 50, opacity: 0 },
      visible: {
        x: 0,
        opacity: 1,
        transition: {
          duration: 0.8,
          ease: "easeOut"
        }
      }
    },
    circleAnim: {
      hidden: { scale: 0 },
      visible: {
        scale: 1,
        transition: {
          duration: 0.8,
          type: "spring",
          bounce: 0.4
        }
      }
    }
  }), []);

  // Memoize bullet points content
  const bulletPoints = useMemo(() => [
    {
      text: "25+ Years Combined Expertise: Our elite team delivers real-time insights for competitive edge.",
      key: "expertise"
    },
    {
      text: "Proven Results: Clients see 42% better returns versus market benchmarks.",
      key: "results"
    }
  ], []);

  // Memoize stars array
  const stars = useMemo(() => Array(5).fill(0), []);

  return (
    <div className="bg-[#F3F4F6] mx-auto max-w-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden mt-6" >
        <div className="bg-gradient-to-br from-[#f0f9f3] to-[#e1f3e8]">
          <div className="max-w-full mx-auto">
            <div
              ref={ref}
              className="flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-16 px-4 sm:px-6 lg:py-10"
            >
              {/* Left Content Column */}
              <motion.div
                className="w-full lg:w-7/12 xl:w-6/12 relative z-10 order-2 lg:order-1 mb-6"
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                variants={animationVariants.container}
              >
                {/* Badge */}
                <motion.div className="mb-3 lg:mb-2" variants={animationVariants.item}>
                  <span className="inline-block px-3 py-1 text-xs sm:text-sm font-semibold text-[#0e4322] bg-[#d4edda] rounded-full">
                    Meet Your Financial Allies
                  </span>
                </motion.div>

                {/* Main Heading */}
                <motion.h2
                  className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#0e4322] font-amiriQuran leading-tight mb-4 lg:mb-6"
                  variants={animationVariants.item}
                >
                  The Brains Behind <span className="text-[#2e8544] relative inline-block">Your Success
                    <svg className="absolute -bottom-1 lg:-bottom-2 left-0 w-full h-1.5 lg:h-2" viewBox="0 0 200 20">
                      <path d="M0,10 Q50,20 100,10 T200,10" stroke="#2e8544" strokeWidth="3" fill="transparent" />
                    </svg>
                  </span>
                </motion.h2>

                {/* Bullet Points */}
                <motion.div className="space-y-4 lg:space-y-6 mb-8 lg:mb-10" variants={animationVariants.container}>
                  <motion.div
                    className="relative pl-7 lg:pl-8 group"
                    variants={animationVariants.item}
                  >
                    <div className="absolute left-0 top-1 lg:top-2 h-5 w-5 lg:h-6 lg:w-6 rounded-full bg-[#2e8544] flex items-center justify-center">
                      <CheckIcon />
                    </div>
                    <p className="text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-[#175f33] font-amiriQuran">
                      <strong>Our Approach:</strong> Our elite team delivers real-time insights for competitive edge.
                    </p>
                  </motion.div>
                </motion.div>

                {/* Buttons */}
                <motion.div
                  className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 lg:mt-12"
                  variants={animationVariants.item}
                >
                  <motion.button
                    onClick={() => navigate('/contactus')}
                    className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-[#0e4322] text-[#0e4322] hover:bg-[#27af5b] hover:text-white rounded-lg font-semibold text-base sm:text-lg transition-colors duration-300 flex items-center justify-center gap-2"
                    whileHover={{
                      backgroundColor: "#0e4322",
                      color: "#fff"
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <UserAddIcon />
                    Join Our Team
                  </motion.button>
                </motion.div>
              </motion.div>

              {/* Right Image Column */}
              <motion.div
                className="w-full lg:w-5/12 xl:w-6/12 relative order-1 lg:order-2 mb-8 lg:mb-0"
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                variants={animationVariants.imageAnim}
              >
                <motion.div
                  className="relative rounded-xl lg:rounded-2xl overflow-hidden shadow-lg lg:shadow-2xl"
                  whileHover={{ scale: 1.01 }}
                >
                  <motion.img
                    src={stockimg2}
                    alt="Team Collaboration"
                    className="w-full h-auto object-cover aspect-square lg:aspect-auto"
                    loading="lazy"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e4322]/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-8 text-white">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-1 lg:mb-2">"Our team lives and breathes your success"</h3>
                  </div>
                </motion.div>

                {/* Decorative Circles */}
                <motion.div
                  className="absolute -bottom-6 -left-6 w-20 h-20 lg:w-32 lg:h-32 rounded-full bg-[#d4edda] opacity-70 z-0"
                  initial="hidden"
                  animate={inView ? "visible" : "hidden"}
                  variants={animationVariants.circleAnim}
                />
                <motion.div
                  className="absolute -top-6 -right-6 w-24 h-24 lg:w-40 lg:h-40 rounded-full bg-[#d4edda] opacity-70 z-0"
                  initial="hidden"
                  animate={inView ? "visible" : "hidden"}
                  variants={animationVariants.circleAnim}
                  transition={{ delay: 0.2 }}
                />
              </motion.div>
            </div>
          </div>
        </div>

        <div className="absolute left-0 top-0 w-full h-full overflow-hidden -z-20 opacity-10">
          <div className="absolute right-0 top-0 w-1/3 h-full bg-[#2e8544] rounded-full filter blur-3xl"></div>
        </div>
      </div>

      <div className="flex-wrap items-start justify-center gap-6 sm:gap-8 lg:contents mx-auto grid max-w-2xl grid-cols-1 gap-x-12 gap-y-16 lg:mx-0 lg:min-w-full lg:max-w-none lg:flex-none lg:gap-y-8">
        <div className="contents lg:col-span-2 lg:col-end-2 lg:ml-auto lg:flex py-16 lg:w-auto lg:items-start lg:justify-end lg:gap-x-8">
          <div className="order-first flex w-full justify-end self-end sm:hidden">
            <img
              src={stockimg2}
              alt="Stock Analysis"
              className="aspect-[4/3] w-full rounded-2xl bg-gray-50 object-cover shadow-lg"
              loading="lazy"
            />
          </div>
          <div className="order-first flex w-full sm:w-64 lg:w-auto justify-end self-end">
            <img
              src={stockimg3}
              alt="Stock Analysis"
              className="aspect-[4/3] w-full sm:w-[24rem] rounded-2xl bg-gray-50 object-cover shadow-lg"
              loading="lazy"
            />
          </div>
          <div className="flex w-full sm:w-96 lg:w-auto justify-end">
            <img
              src={stockimg4}
              alt="Financial Planning"
              className="aspect-[7/5] w-full sm:w-[37rem] rounded-2xl bg-gray-50 object-cover shadow-lg"
              loading="lazy"
            />
          </div>
          <div className="hidden sm:block sm:w-full sm:flex-auto lg:w-auto lg:flex-none">
            <img
              src={stockimg5}
              alt="Market Insights"
              className="aspect-[4/3] w-full sm:w-[24rem] rounded-2xl bg-gray-50 object-cover shadow-lg"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(Content);